// src/services/costs/excelImport.js
// Parses cost estimation Excel files into the tree structure used by EstimateEditor
import * as XLSX from "xlsx";
import { calculateTotal, aggregateTree } from "./calculations.js";

// Known standard columns by header text (case-insensitive, trimmed)
const STANDARD_HEADERS = {
  "bkp-nr": "code",
  "bkp": "code",
  "name": "name",
  "bezeichnung": "name",
  "source": "source",
  "quelle": "source",
  "länge": "length",
  "laenge": "length",
  "breite": "width",
  "höhe": "height",
  "hoehe": "height",
  "anzahl": "quantity",
  "menge": "quantity",
  "einheit": "unit",
  "ep": "unitPrice",
  "einheitspreis": "unitPrice",
  "faktor": "factor",
  "total (chf)": "total",
  "total": "total",
  "total chf": "total",
};

// Map Excel unit strings to internal unit codes
const UNIT_MAP = {
  "pauschal": "pl",
  "pauchal": "pl",  // common typo
  "pausch": "pl",
  "stück": "st",
  "stuck": "st",
  "stk": "st",
  "laufmeter": "m",
  "m": "m",
  "m2": "m2",
  "m²": "m2",
  "m3": "m3",
  "m³": "m3",
  "stunden": "h",
  "h": "h",
  "tonne": "t",
  "t": "t",
  "kilogramm": "kg",
  "kg": "kg",
};

function normalizeUnit(raw) {
  if (!raw) return "pl";
  // Trailing "." entfernen ("Stk." -> "stk", "m2." -> "m2"), damit UNIT_MAP matcht.
  const cleaned = String(raw).trim().toLowerCase().replace(/\.+$/, "");
  return UNIT_MAP[cleaned] || "pl";
}

function normalizeCode(raw) {
  if (raw == null || raw === "") return null;
  let code = String(raw).trim();
  // BKP codes must start with a digit
  if (!/^\d/.test(code)) return null;
  // BKP codes can come as numbers (113.1) or strings ("211.51")
  // Remove leading zeros on the integer part but keep structure
  if (code.match(/^\d+$/)) {
    code = String(parseInt(code, 10));
  } else if (code.includes(".")) {
    const [intPart, decPart] = code.split(".");
    code = `${parseInt(intPart, 10)}.${decPart}`;
  }
  return code;
}

function getLevel(code) {
  if (!code) return 0;
  if (code.includes(".")) {
    const afterDot = code.split(".")[1];
    if (afterDot.length === 1) return 4;
    if (afterDot.length >= 2) return 5;
  }
  return code.split(".")[0].length; // 1, 2, or 3
}

function getParentCode(code) {
  if (!code) return null;
  if (code.includes(".")) {
    const parts = code.split(".");
    const afterDot = parts[1];
    if (afterDot.length >= 2) return `${parts[0]}.${afterDot.charAt(0)}`;
    return parts[0]; // e.g., "113.1" -> "113"
  }
  if (code.length > 1) return code.substring(0, code.length - 1);
  return null;
}

/**
 * Parse an Excel file (File object) into a structured import result.
 * Returns { meta, tree, customColumns, rows } where:
 *   - meta: { project, phase, accuracy, date, author }
 *   - tree: { [code]: node } ready for the editor
 *   - customColumns: [{ headerName, values: { [code]: string } }]
 *   - rows: raw parsed rows for preview
 */
export async function parseExcelFile(file) {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: "array", cellFormula: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const range = XLSX.utils.decode_range(ws["!ref"] || "A1");

  // --- Extract metadata from top rows ---
  const meta = { project: "", phase: "", accuracy: "", date: "", author: "" };
  const getCell = (r, c) => ws[XLSX.utils.encode_cell({ r, c })];
  const getCellValue = (r, c) => {
    const cell = ws[XLSX.utils.encode_cell({ r, c })];
    return cell ? cell.v : "";
  };

  // Scan first 10 rows for metadata
  for (let r = 0; r <= Math.min(range.e.r, 10); r++) {
    const label = String(getCellValue(r, 0)).trim().toLowerCase();
    const value = getCellValue(r, 1);
    if (label === "projekt") meta.project = String(value).trim();
    else if (label === "phase") meta.phase = String(value).trim();
    else if (label.includes("genauigkeit")) meta.accuracy = String(value).trim();
    else if (label === "datum") {
      if (typeof value === "number") {
        const parsed = XLSX.SSF.parse_date_code(value);
        meta.date = `${parsed.y}-${String(parsed.m).padStart(2, "0")}-${String(parsed.d).padStart(2, "0")}`;
      } else {
        meta.date = String(value).trim();
      }
    } else if (label.includes("verfasser")) meta.author = String(value).trim();
  }

  // --- Find header row ---
  let headerRow = -1;
  for (let r = 0; r <= Math.min(range.e.r, 15); r++) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      const val = String(getCellValue(r, c)).trim().toLowerCase();
      if (val === "bkp-nr" || val === "bkp") {
        headerRow = r;
        break;
      }
    }
    if (headerRow >= 0) break;
  }
  if (headerRow < 0) throw new Error("Keine Header-Zeile mit 'BKP-Nr' gefunden");

  // --- Parse headers ---
  const headers = [];
  const columnMapping = []; // { col, field, isCustom, headerName }
  for (let c = range.s.c; c <= range.e.c; c++) {
    const raw = String(getCellValue(headerRow, c)).trim();
    const key = raw.toLowerCase();
    headers.push(raw);

    if (!raw) {
      columnMapping.push(null);
      continue;
    }

    const standardField = STANDARD_HEADERS[key];
    if (standardField) {
      columnMapping.push({ col: c, field: standardField, isCustom: false, headerName: raw });
    } else {
      columnMapping.push({ col: c, field: null, isCustom: true, headerName: raw });
    }
  }

  // Identify custom columns
  const customColumnHeaders = columnMapping
    .filter((m) => m && m.isCustom)
    .map((m) => m.headerName);

  // --- Parse data rows ---
  const rows = [];
  const tree = {};
  const customColumnValues = {}; // { headerName: { code: value } }
  for (const h of customColumnHeaders) {
    customColumnValues[h] = {};
  }

  for (let r = headerRow + 1; r <= range.e.r; r++) {
    // Find BKP code
    const codeColMapping = columnMapping.find((m) => m && m.field === "code");
    if (!codeColMapping) continue;
    const rawCode = getCellValue(r, codeColMapping.col);
    const code = normalizeCode(rawCode);

    // Skip empty rows and TOTAL row
    if (!code) {
      // Check if it's a "Total" summary row
      const nameCol = columnMapping.find((m) => m && m.field === "name");
      if (nameCol) {
        const nameVal = String(getCellValue(r, nameCol.col)).trim().toLowerCase();
        if (nameVal === "total") continue;
      }
      continue;
    }

    const rowData = { code };
    const rowRaw = { code };

    for (const mapping of columnMapping) {
      if (!mapping || mapping.field === "code") continue;
      const val = getCellValue(r, mapping.col);

      if (mapping.isCustom) {
        const strVal = val != null && val !== "" && val !== 0 ? String(val).trim() : "";
        if (strVal) customColumnValues[mapping.headerName][code] = strVal;
        rowRaw[mapping.headerName] = strVal;
      } else {
        rowRaw[mapping.field] = val;
        if (mapping.field === "quantity") {
          // If the cell contains a PRODUCT() formula, it's derived from dimensions
          // -> ignore and use 1, the dimensions carry the actual values
          const cell = getCell(r, mapping.col);
          const formula = cell?.f || "";
          if (formula.toUpperCase().includes("PRODUCT")) {
            rowData.quantity = 0;
          } else {
            rowData.quantity = parseFloat(val) || 0;
          }
        } else if (mapping.field === "unit") {
          rowData.unit = normalizeUnit(val);
        } else if (["length", "width", "height", "unitPrice", "factor", "total"].includes(mapping.field)) {
          rowData[mapping.field] = parseFloat(val) || 0;
        } else {
          rowData[mapping.field] = val != null ? String(val).trim() : "";
        }
      }
    }

    const level = getLevel(code);
    const parentCode = getParentCode(code);

    const node = {
      code,
      name: rowData.name || "",
      source: rowData.source || "",
      length: rowData.length || 0,
      width: rowData.width || 0,
      height: rowData.height || 0,
      quantity: rowData.quantity || 0,
      unit: rowData.unit || "pl",
      unitPrice: rowData.unitPrice || 0,
      factor: rowData.factor || 1,
      totalComputed: 0,
      active: false,
      level,
      parentCode,
      children: [],
      tags: [],
      customFields: {},
      phase: "default",
      isUserAdded: false,
    };

    node.totalComputed = calculateTotal(node);
    // Track Excel total for mismatch detection
    const excelTotal = rowData.total || 0;
    node._excelTotal = excelTotal;
    tree[code] = node;

    rows.push({ ...rowRaw, _code: code, _level: level });
  }

  // --- Build children arrays ---
  for (const node of Object.values(tree)) {
    if (node.parentCode && tree[node.parentCode]) {
      if (!tree[node.parentCode].children.includes(node.code)) {
        tree[node.parentCode].children.push(node.code);
      }
    }
  }

  // --- Activate smallest children (leaf nodes) ---
  for (const node of Object.values(tree)) {
    const isLeaf = !node.children || node.children.length === 0;
    if (isLeaf && node.totalComputed > 0) {
      node.active = true;
    }
  }

  // For parent nodes that have no active children but have a total value,
  // check if they should be activated (they ARE the leaf in the imported data)
  for (const node of Object.values(tree)) {
    if (node.children && node.children.length > 0) {
      const hasActiveChild = node.children.some((c) => tree[c]?.active);
      if (!hasActiveChild && node.totalComputed > 0) {
        // This parent has no children with values - it's effectively a leaf
        // But only activate if none of its children exist in the tree
        const allChildrenEmpty = node.children.every((c) => !tree[c] || tree[c].totalComputed === 0);
        if (allChildrenEmpty) {
          node.active = true;
        }
      }
    }
  }

  // --- Aggregate parent totals from children ---
  const aggregated = aggregateTree(tree);

  // --- Detect mismatches between calculated and Excel totals ---
  // Fixe Toleranz von 0.5 CHF: faengt Rappen-Rundungsdrift bei der Aggregation ab,
  // meldet aber jede groessere Diskrepanz - unabhaengig von der Positionsgroesse.
  // (Frueherer Versuch mit max(0.5, excelTotal * 0.0001) hat die Toleranz bei grossen
  //  Summen vergroessert statt verkleinert und damit Mismatches verschluckt statt
  //  aufzudecken; Logik wieder auf den bewaehrten fixen Wert zurueckgesetzt.)
  const mismatches = {};
  for (const node of Object.values(aggregated)) {
    const excelTotal = node._excelTotal || 0;
    const calcTotal = node.totalComputed || 0;
    if (excelTotal > 0 && Math.abs(excelTotal - calcTotal) > 0.5) {
      mismatches[node.code] = { excelTotal, calcTotal };
    }
    // Clean up temp field
    delete aggregated[node.code]._excelTotal;
  }

  // Build custom columns result
  const customColumns = customColumnHeaders.map((headerName) => ({
    headerName,
    values: customColumnValues[headerName],
  }));

  return { meta, tree: aggregated, customColumns, rows, headers: columnMapping.filter(Boolean), mismatches };
}
